import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useMediaQuery } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { Check, Star, Sparkles, Loader2, Lock } from "lucide-react";
import { useState, useRef } from "react";
import confetti from "canvas-confetti";
import NumberFlow from "@number-flow/react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { STRIPE_TIERS } from "@/constants/stripeTiers";
import { toast } from "@/hooks/use-toast";
import { logEdgeFunctionError } from "@/utils/sentry";

interface PricingFeature {
  label: string;
  value?: string;
  subtitle?: string;
  bonus?: string;
}

type PlanTheme = "blue" | "orange" | "gold";

interface PricingPlan {
  name: string;
  price: string;
  yearlyPrice: string;
  yearlySavings: string;
  period: string;
  features: PricingFeature[];
  description: string;
  buttonText: string;
  isPopular: boolean;
  extraFeatures?: string[];
  badge?: string;
  theme: PlanTheme;
}

const THEME_COLORS: Record<PlanTheme, {
  accent: string;
  price: string;
  checkBg: string;
  checkIcon: string;
  valueBold: string;
  border: string;
  ring: string;
  badgeBg: string;
  nameTxt: string;
  btnClass: string;
}> = {
  blue: {
    accent: "text-[#0080FF]",
    price: "text-[#0080FF]",
    checkBg: "bg-blue-100",
    checkIcon: "text-[#0080FF]",
    valueBold: "text-[#0080FF]",
    border: "border-gray-200",
    ring: "",
    badgeBg: "",
    nameTxt: "text-[#0080FF]",
    btnClass: "bg-[#0080FF] hover:bg-[#006AD5] text-white py-5 text-base shadow-md hover:shadow-lg",
  },
  orange: {
    accent: "text-orange-600",
    price: "text-orange-600",
    checkBg: "bg-orange-100",
    checkIcon: "text-orange-600",
    valueBold: "text-orange-600",
    border: "border-orange-500",
    ring: "ring-4 ring-orange-500/10",
    badgeBg: "bg-orange-500",
    nameTxt: "text-orange-600",
    btnClass: "bg-orange-500 hover:bg-orange-600 text-white py-6 text-lg shadow-lg hover:shadow-xl",
  },
  gold: {
    accent: "text-yellow-500",
    price: "text-yellow-500",
    checkBg: "bg-yellow-100",
    checkIcon: "text-yellow-500",
    valueBold: "text-yellow-500",
    border: "border-yellow-400",
    ring: "ring-2 ring-yellow-400/20",
    badgeBg: "bg-gradient-to-r from-yellow-400 to-amber-400",
    nameTxt: "text-yellow-500",
    btnClass: "bg-gradient-to-r from-yellow-400 to-amber-400 hover:from-yellow-500 hover:to-amber-500 text-white font-bold py-5 text-base shadow-md hover:shadow-lg",
  },
};


const PLANS: PricingPlan[] = [
  {
    name: "Starter",
    price: "59",
    yearlyPrice: "47",
    yearlySavings: "149",
    period: "miesiąc",
    features: [
      { label: "Kreator scenariuszy Basic", value: "15 scenariuszy/mies." },
      { label: "Kreator scenariuszy Pro", value: "10 scenariuszy/mies.", subtitle: "fact-checking, wybór odbiorcy, długość scenariusza, tryby narracji: swobodny, neutralny, humorystyczny, profesjonalny" },
      { label: "Gotowe scenariusze wideo", value: "do 3 źródeł inspiracji" },
      { label: "Śledzenie konkurencji", value: "do 3 profili" },
      { label: "Analityka wzrostu", value: "wyprzedzająca Instagram o 24h" },
      { label: "Biblioteka pomysłów", value: "30 ostatnich dni" },
      { label: "Prompter", value: "nagrywaj bez przeszkód, bez limitu" },
      { label: "Hooków + nakładek tekstowych", value: "10 + 10 na scenariusz" },
      { label: "Zaskakujących faktów", value: "do 5 na scenariusz" },
      { label: "Fact-checking treści", value: "w scenariuszach" },
    ],
    description: "Idealny dla małych firm i twórców",
    buttonText: "Wypróbuj 5 dni za darmo",
    isPopular: false,
    theme: "blue",
  },
  {
    name: "Pro",
    price: "149",
    yearlyPrice: "112",
    yearlySavings: "449",
    period: "miesiąc",
    features: [
      { label: "Kreator scenariuszy Basic", value: "45 scenariuszy/mies.", bonus: "3× więcej" },
      { label: "Kreator scenariuszy Pro", value: "50 scenariuszy/mies.", subtitle: "fact-checking, wybór odbiorcy, długość scenariusza, tryby narracji: swobodny, neutralny, humorystyczny, profesjonalny", bonus: "5× więcej" },
      { label: "Gotowe scenariusze wideo", value: "do 6 źródeł inspiracji", bonus: "2× więcej" },
      { label: "Śledzenie konkurencji", value: "do 6 profili", bonus: "2× więcej" },
      { label: "Analityka wzrostu", value: "wyprzedzająca Instagram o 24h" },
      { label: "Biblioteka pomysłów", value: "bez limitu" },
      { label: "Prompter", value: "nagrywaj bez przeszkód, bez limitu" },
      { label: "Hooków + nakładek tekstowych", value: "10 + 10 na scenariusz" },
      { label: "Zaskakujących faktów", value: "do 5 na scenariusz" },
      { label: "Fact-checking treści", value: "w scenariuszach" },
    ],
    description: "Dla rozwijających się firm i zespołów",
    buttonText: "Wypróbuj 5 dni za darmo",
    isPopular: true,
    theme: "orange",
  },
  {
    name: "Enterprise",
    price: "699",
    yearlyPrice: "595",
    yearlySavings: "1248",
    period: "miesiąc",
    features: [
      { label: "Wszystko z Pro" },
      { label: "Spersonalizowany ton narracji", value: "Twój styl wypowiedzi" },
      { label: "Przedpremierowy dostęp", value: "do nowych funkcjonalności" },
      { label: "Dedykowany opiekun konta" },
      { label: "Konsultacje z ekspertem", value: "2 godziny miesięcznie" },
      { label: "Rabat na realizację wideo", value: "20%" },
    ],
    description: "Dla dużych organizacji i agencji",
    buttonText: "Skontaktuj się z nami",
    isPopular: false,
    badge: "Premium",
    theme: "gold",
  },
];

/** Render a structured feature row with label on top + value below */
const FeatureRow = ({ feature, theme }: { feature: PricingFeature; theme: PlanTheme }) => {
  const colors = THEME_COLORS[theme];

  return (
    <div className="flex flex-col">
      <span className="text-sm text-gray-500 leading-snug">{feature.label}</span>
      {feature.value && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className={cn("text-sm font-bold leading-snug", colors.valueBold)}>
            {feature.value}
          </span>
          {feature.bonus && (
            <span className="text-[11px] font-bold text-white bg-orange-500 px-1.5 py-0.5 rounded-full whitespace-nowrap">
              {feature.bonus}
            </span>
          )}
        </div>
      )}
      {feature.subtitle && (
        <span className="text-xs text-gray-400 leading-snug mt-0.5">{feature.subtitle}</span>
      )}
    </div>
  );
};

const PricingSection = () => {
  const [isMonthly, setIsMonthly] = useState(true);
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const switchRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);

  const handleCheckout = async (planName: string) => {
    if (!user) {
      const billing = isMonthly ? "monthly" : "yearly";
      localStorage.setItem("selected_plan", JSON.stringify({ planName, billing }));
      navigate("/auth?mode=register&trial=true");
      return;
    }

    setLoadingPlan(planName);
    try {
      let priceId: string;
      const billing = isMonthly ? "monthly" : "yearly";

      if (planName === "Starter") {
        priceId = STRIPE_TIERS.starter[billing].priceId;
      } else if (planName === "Pro") {
        priceId = STRIPE_TIERS.pro[billing].priceId;
      } else {
        navigate("/auth?mode=register&trial=true");
        return;
      }

      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { priceId },
      });

      if (error) throw error;
      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (err) {
      const message = logEdgeFunctionError('create-checkout', err, { planName });
      toast({
        title: "Błąd",
        description: message,
        variant: "destructive",
      });
    } finally {
      setLoadingPlan(null);
    }
  };

  const handleToggle = (checked: boolean) => {
    setIsMonthly(!checked);
    if (checked && switchRef.current) {
      const rect = switchRef.current.getBoundingClientRect();
      const x = rect.left + rect.width / 2;
      const y = rect.top + rect.height / 2;

      confetti({
        particleCount: 50,
        spread: 60,
        origin: {
          x: x / window.innerWidth,
          y: y / window.innerHeight,
        },
        colors: ["#f97316", "#fb923c", "#fdba74", "#fed7aa"],
        ticks: 200,
        gravity: 1.2,
        decay: 0.94,
        startVelocity: 30,
        shapes: ["circle"],
      });
    }
  };

  return (
    <section
      id="pricing"
      className="relative py-20 md:py-28 px-4 overflow-hidden scroll-mt-24"
    >
      {/* Brand blue background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0080FF] to-[#006AD5]" />
      {/* Smooth fade-in from CondensedStats' peach ending above */}
      <div
        className="absolute inset-x-0 top-0 h-32 md:h-40 pointer-events-none"
        style={{ background: "linear-gradient(to bottom, #F5A07A, transparent)" }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0"
        style={{
          background: "radial-gradient(ellipse 60% 40% at 50% 30%, rgba(255,255,255,0.1) 0%, transparent 70%)",
        }}
      />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tighter leading-[1.05] text-white mb-5">
            Wybierz plan dla siebie
          </h2>
          <p className="text-lg md:text-xl text-blue-100 max-w-2xl mx-auto leading-relaxed">
            Transparentne ceny. Bez ukrytych kosztów.
          </p>
        </div>

        {/* Toggle */}
        <div className="flex items-center justify-center gap-4 mb-12">
          <span
            className={cn(
              "text-base font-medium transition-colors",
              isMonthly ? "text-white" : "text-blue-200"
            )}
          >
            Miesięcznie
          </span>
          <div className="relative">
            <Switch
              ref={switchRef}
              checked={!isMonthly}
              onCheckedChange={handleToggle}
              className="data-[state=checked]:bg-orange-500"
            />
          </div>
          <span
            className={cn(
              "text-base font-medium transition-colors",
              !isMonthly ? "text-white" : "text-blue-200"
            )}
          >
            Rocznie{" "}
            <span className="text-orange-400 font-bold">(Oszczędzasz do 25%)</span>
          </span>
        </div>

        {/* Early-adapter seats urgency banner */}
        <div className="flex justify-center mb-14">
          <div className="flex items-center gap-2.5 bg-orange-500 rounded-full px-6 py-3 shadow-xl">
            <Sparkles className="w-4 h-4 text-white flex-shrink-0" />
            <span className="text-sm font-bold text-white tracking-wide">
              Letnia promocja — ograniczona liczba miejsc
            </span>
          </div>
        </div>

        {/* Cards grid */}
        <div className="grid md:grid-cols-3 gap-6 md:gap-8 items-start">
          {PLANS.map((plan, index) => {
            const colors = THEME_COLORS[plan.theme];
            return (
            <div
              key={index}
              className={cn(
                "relative flex flex-col rounded-2xl transition-all duration-300",
                plan.isPopular
                  ? cn("bg-white border-2 shadow-2xl md:scale-105 z-20", colors.border, colors.ring)
                  : cn("bg-white border shadow-md hover:shadow-lg hover:-translate-y-1", colors.border, colors.ring)
              )}
            >
              {/* Popular / Premium badge */}
              {(plan.isPopular || plan.badge) && (
                <div className="absolute -top-4 left-0 right-0 flex justify-center">
                  {plan.isPopular ? (
                    <span className={cn("text-white text-sm px-4 py-1.5 font-bold rounded-full flex items-center gap-1.5 shadow-lg", colors.badgeBg)}>
                      Najpopularniejsze
                    </span>
                  ) : plan.badge ? (
                    <span className={cn("text-white text-sm px-4 py-1.5 font-bold rounded-full flex items-center gap-1.5 shadow-lg", colors.badgeBg || "bg-gray-900")}>
                      {plan.badge}
                    </span>
                  ) : null}
                </div>
              )}

              <div className="flex flex-col flex-1 p-6 md:p-8">
                {/* Plan name */}
                <div className="mb-6">
                  <h3 className={cn("text-2xl font-bold tracking-tight leading-tight mb-2", colors.nameTxt)}>
                    {plan.name}
                  </h3>
                  <p className="text-base text-gray-500">{plan.description}</p>
                </div>

                {/* Price with anchor effect */}
                <div className="mb-1">
                  {!isMonthly && plan.price !== plan.yearlyPrice && (
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xl text-gray-400 line-through font-semibold">
                        {plan.price} PLN
                      </span>
                    </div>
                  )}
                  <div className="flex items-baseline gap-1.5">
                    <NumberFlow
                      value={parseInt(isMonthly ? plan.price : plan.yearlyPrice)}
                      format={{ useGrouping: false }}
                      transformTiming={{
                        duration: 500,
                        easing: "ease-out",
                      }}
                      className={cn(
                        "text-5xl md:text-6xl font-black tabular-nums tracking-tighter leading-none",
                        colors.price
                      )}
                    />
                    <div className="flex flex-col">
                      <span className="text-base text-gray-500 font-medium">
                        PLN / {plan.period}
                      </span>
                      <span className="text-xs text-gray-400">cena końcowa</span>
                    </div>
                  </div>
                </div>

                {/* Billing info + savings */}
                <div className="flex items-center gap-2 mb-4">
                  <p className="text-sm text-gray-400">
                    {isMonthly ? "rozliczane miesięcznie" : "rozliczane rocznie"}
                  </p>
                  {!isMonthly && (
                    <span className="text-xs font-bold text-emerald-700 bg-emerald-100 px-2.5 py-1 rounded-full whitespace-nowrap">
                      Oszczędzasz {plan.yearlySavings} PLN
                    </span>
                  )}
                </div>

                {/* CTA Button */}
                <Button
                  disabled={loadingPlan === plan.name}
                  className={cn(
                    "w-full rounded-xl font-bold transition-all duration-300 active:scale-[0.98] mb-6",
                    colors.btnClass
                  )}
                  onClick={() => plan.name === "Enterprise" ? navigate("/kontakt") : handleCheckout(plan.name)}
                >
                  {loadingPlan === plan.name ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    plan.buttonText
                  )}
                </Button>

                {plan.name !== "Enterprise" && (
                  <p className="text-xs text-center text-gray-400 -mt-4 mb-4">
                    5 dni za darmo, potem {isMonthly ? plan.price : plan.yearlyPrice} PLN/{plan.period}
                  </p>
                )}

                {/* Divider */}
                <div className="h-px bg-gray-100 mb-6" />

                {/* Features */}
                <ul className="space-y-4 mb-6 flex-1">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className={cn(
                        "w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-1",
                        colors.checkBg
                      )}>
                        <Check className={cn("w-3 h-3", colors.checkIcon)} />
                      </div>
                      <FeatureRow feature={feature} theme={plan.theme} />
                    </li>
                  ))}
                </ul>

                {/* Extra features */}
                {plan.extraFeatures && plan.extraFeatures.length > 0 && (
                  <div className="bg-orange-50 border-l-4 border-orange-500 rounded-lg p-4 mb-6">
                    <p className="text-sm font-bold text-orange-600 uppercase tracking-wide mb-3">
                      Bonus
                    </p>
                    <ul className="space-y-2">
                      {plan.extraFeatures.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <Sparkles className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-900 font-medium">
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
            );
          })}
        </div>

        {/* Trust badge */}
        <div className="flex justify-center mt-12">
          <div className="flex items-center gap-2 text-base text-blue-100">
            <Lock className="w-5 h-5" />
            <span>Bezpieczne zakupy przez</span>
            <span className="bg-white rounded px-2 py-0.5 inline-flex items-center">
              <svg className="h-6" viewBox="0 0 468 222.5" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Stripe">
                <path d="M414 113.4c0-25.6-12.4-45.8-36.1-45.8-23.8 0-38.2 20.2-38.2 45.6 0 30.1 17 45.3 41.4 45.3 11.9 0 20.9-2.7 27.7-6.5v-20c-6.8 3.4-14.6 5.5-24.5 5.5-9.7 0-18.3-3.4-19.4-15.2h48.9c0-1.3.2-6.5.2-8.9zm-49.4-9.5c0-11.3 6.9-16 13.2-16 6.1 0 12.6 4.7 12.6 16h-25.8zM301.1 67.6c-9.8 0-16.1 4.6-19.6 7.8l-1.3-6.2h-22v116.6l25-5.3.1-28.3c3.6 2.6 8.9 6.3 17.7 6.3 17.9 0 34.2-14.4 34.2-46.1-.1-29-16.6-44.8-34.1-44.8zm-6 68.9c-5.9 0-9.4-2.1-11.8-4.7l-.1-37.1c2.6-2.9 6.2-4.9 11.9-4.9 9.1 0 15.4 10.2 15.4 23.3 0 13.4-6.2 23.4-15.4 23.4zM223.8 61.7l25.1-5.4V36l-25.1 5.3zM223.8 69.3h25.1v87.5h-25.1zM196.9 76.7l-1.6-7.4h-21.6v87.5h25V97.5c5.9-7.7 15.9-6.3 19-5.2v-23c-3.2-1.2-14.9-3.4-20.8 7.4zM146.9 47.6l-24.4 5.2-.1 80.1c0 14.8 11.1 25.7 25.9 25.7 8.2 0 14.2-1.5 17.5-3.3V135c-3.2 1.3-19 5.9-19-8.9V90.6h19V69.3h-19l.1-21.7zM79.3 94.7c0-3.9 3.2-5.4 8.5-5.4 7.6 0 17.2 2.3 24.8 6.4V72.2c-8.3-3.3-16.5-4.6-24.8-4.6C67.5 67.6 52 78.8 52 95.4c0 25.9 35.7 21.8 35.7 33 0 4.6-4 6.1-9.6 6.1-8.3 0-18.9-3.4-27.3-8v23.8c9.3 4 18.7 5.7 27.3 5.7 20.8 0 35.1-10.3 35.1-27.3-.1-28-35.9-23-35.9-33.8z" fill="#635BFF"/>
              </svg>
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
